import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SortingState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import StatusChip from "components/Chips/StatusChip";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  downloadDispatchPdf,
  fetchWarehouseDispatch,
} from "slices/Warehouse/Dispatch/Dispatch/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

type IssueToLabourProps = {
  pageIndex: number;
  loading: boolean;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const IssueToLabour: React.FC<IssueToLabourProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "Dispatch";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Redux selectors
  const selectDispatch = createSelector(
    (state: any) => state.WarehouseDispatchReducer,
    (item) => ({
      list: item.list,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(selectDispatch);

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.dispatch
  );

  // Fetch IssueToLabour list
  useEffect(() => {
    dispatch(
      fetchWarehouseDispatch({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseIdSearch || "",
      })
    );
  }, [pageIndex, pageSize, sorting, search, filterValue, warehouseIdSearch]);

  const handleDispatchStatus = useCallback(
    (item: any) => {
      showCustomSwal({
        title: "Updating...",
        text: "Please wait while updating dispatch status.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      Axios.patch(
        `${api?.warehouse?.dispatch?.dispatch_done?.update_dispatch_status}/${item.dispatch_id}/delivered`,
        { status: item.status }
      )
        .then((response) => {
          // 🔄 Refresh list using Redux dispatch with current warehouse value
          return dispatch(
            fetchWarehouseDispatch({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? (sortDirection as "asc" | "desc") : "",
              search,
              filters: filterValue,
              warehouse_id: warehouseIdSearch || "",
            })
          ).then(() => response); // pass response forward
        })
        .then(() => {
          // 🟢 SUCCESS MESSAGE
          showCustomSwal({
            icon: "success",
            title: "Status Updated",
            text: "Dispatch status has been updated successfully!",
          });
        })
        .catch((error) => {
          // 🔴 ERROR MESSAGE
          showCustomSwal({
            icon: "error",
            title: "Failed to Update",
            text: error.response?.data?.message || "Something went wrong!",
          });
          console.error("Dispatch Update Error:", error);
        });
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
      warehouseIdSearch,
    ]
  );

  const handleDownloadPdf = useCallback(
    async (dispatchRow: any) => {
      const dispatchId = dispatchRow?.dispatch_id;
      if (!dispatchId) return;

      showCustomSwal({
        title: "Downloading PDF...",
        text: "Please wait while we prepare your dispatch invoice.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const { blob, filename } = await dispatch(
          downloadDispatchPdf({
            id: dispatchId,
            dispatchNumber: dispatchRow?.dispatch_number,
          })
        ).unwrap();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.close();
        toast.success("PDF downloaded");
      } catch (error: any) {
        console.error("Dispatch PDF download error:", error);
        // Extract error message from various possible error structures
        const message =
          error?.payload?.message ||
          error?.payload?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to download dispatch PDF. Please contact support if the issue persists.";
        Swal.close();
        showCustomSwal({
          icon: "error",
          title: "Download Failed",
          text: message,
        });
      }
    },
    [dispatch]
  );

  const handleDispatchRevert = useCallback(
    (item: any) => {
      // Show confirmation popup first
      showCustomSwal({
        title: "Are you sure?",
        text: "Do you want to revert this dispatch?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, revert it!",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          // Show loading modal
          showCustomSwal({
            title: "Reverting...",
            text: "Please wait while we process your request.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          Axios.post(
            `${api?.warehouse?.dispatch?.dispatch_done?.revert}/${item.dispatch_id}/revert`
          )
            .then((response) => {
              if (response?.status === 200) {
                // Replace loading modal with success modal
                showCustomSwal({
                  icon: "success",
                  title: "Reverted Successfully",
                  text:
                    response.data?.message || "Dispatch reverted successfully!",
                });

                // Refresh the list with current warehouse value
                dispatch(
                  fetchWarehouseDispatch({
                    page: pageIndex + 1,
                    pageSize,
                    sort: sortField,
                    order: sortField ? (sortDirection as "asc" | "desc") : "",
                    search,
                    filters: filterValue,
                    warehouse_id: warehouseIdSearch || "",
                  })
                );
              } else {
                throw new Error("Invalid API response");
              }
            })
            .catch((error) => {
              // Replace loading modal with error modal
              showCustomSwal({
                icon: "error",
                title: "Failed to revert",
                text:
                  error.response?.data?.message || "Failed to revert dispatch.",
              });
              console.error("Dispatch revert Error:", error);
            });
        }
      });
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
      warehouseIdSearch,
    ]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.dispatch_status?.toLowerCase() !== "delivered" &&
            row?.dispatch_type?.toLowerCase() !== "pos"
          );
        },
        onClick: (dispatch: any) => {
          navigate(
            `/warehouse-dispatch/ready-for-dispatch/edit-dispatch/${dispatch?.order_id}/${dispatch?.dispatch_id}`
          );
        },
        color: "themeColor",
      },
      {
        icon: VisibilityIcon,
        label: "View Details",
        permission: canView,
        onClick: (dispatch: any) => {
          navigate(
            `/warehouse-dispatch/dispatch/view-details/${dispatch?.dispatch_id}`
          );
        },
        color: "themeColor",
      },
      {
        icon: PictureAsPdfIcon,
        label: "Download PDF",
        permission: canView,
        onClick: (dispatch: any) => handleDownloadPdf(dispatch),
        color: "themeColor",
      },
      {
        icon: TaskAltOutlinedIcon,
        label: "Delivery done",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.dispatch_status?.toLowerCase() !== "delivered" &&
            row?.dispatch_type?.toLowerCase() !== "pos"
          );
        },
        onClick: (dispatch: any) => {
          handleDispatchStatus(dispatch);
        },
        color: "themeColor",
      },
      {
        icon: UndoIcon,
        label: "Revert",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.dispatch_status?.toLowerCase() !== "delivered" &&
            row?.dispatch_type?.toLowerCase() !== "pos"
          );
        },
        onClick: (dispatch: any) => {
          handleDispatchRevert(dispatch);
        },
        color: "error",
      },
      {
        icon: AssignmentReturnIcon,
        label: "Sales Order Return",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.dispatch_status?.toLowerCase() === "delivered" &&
            row?.dispatch_type?.toLowerCase() !== "pos"
          );
        },
        onClick: (dispatch: any) => {
          navigate(
            `/warehouse-dispatch/dispatch/sales-order-return/${dispatch?.dispatch_id}`
          );
        },
        color: "error",
      },
    ];
    return items;
  }, [navigate, handleDispatchStatus, handleDispatchRevert]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canView;
    if (!hasPermission) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canView, menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },

      {
        header: "Dispatch ID",
        accessorKey: "dispatch_number",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/dispatch_number/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => row.original.dispatch_number || "-",
      },

      {
        header: "Dispatch Date",
        accessorKey: "dispatch_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date",
        cell: ({ row }: { row: any }) => {
          const date = row.original.dispatch_date
            ? new Date(row.original.dispatch_date).toISOString().split("T")[0]
            : "-";
          return date;
        },
      },

      {
        header: "Order No.",
        accessorKey: "order_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/order_no/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => row.original.order_no || "-",
      },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/quotation_number/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          row.original?.quotation?.quotation_number || "-",
      },
      {
        header: "Dispatch By",
        accessorKey: "created_by_username",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/created_by_username/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          let fullname =
            //   row?.original?.created_by_user?.first_name +
            //   " " +
            //   row?.original?.created_by_user?.last_name;
            // if (fullname.includes("undefined")) {
            //   fullname =
            row?.original?.created_by_user?.username || "-";
          // }
          return <span className="text-truncate">{fullname}</span>;
        },
      },
      {
        header: "Dispatch Status",
        accessorKey: "dispatch_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/dispatch_status/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return <StatusChip status={row?.original?.dispatch_status || "-"} />;
        },
      },
      {
        header: "Has Returned Items",
        accessorKey: "has_returned_items",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const returnStatus = row?.original?.has_returned_items || "no return";
          return <StatusChip status={returnStatus} />;
        },
      },
      {
        header: "Dispatch Type",
        accessorKey: "dispatch_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/dispatch_type/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return <StatusChip status={row?.original?.dispatch_type || "-"} />;
        },
      },
      {
        header: "Invoice No.",
        accessorKey: "invoice_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/invoice_no/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => row.original.invoice_no || "-",
      },

      {
        header: "Issue From",
        accessorKey: "issued_from_warehouse.warehouse_name",
        enableSorting: false,
        enableColumnFilter: false,
        filterType: "dropdown",
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/issued_from_warehouse.warehouse_name/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          row.original?.issued_from_warehouse?.warehouse_name || "-",
      },

      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api?.warehouse?.dispatch?.dispatch_done?.filter_dorpdown}/customer_name/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          row.original?.customer_details?.customer_name || "-",
      },

      {
        header: "Remark",
        accessorKey: "remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remark}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remark-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "Created By / Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        // filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/created_at/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username || "-";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By / Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        // filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/created_at/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username || "-";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },

      ...DynamicAction,
    ],
    [dispatch, list, canView, pageIndex, pageSize, warehouseIdSearch]
  );

  return (
    <>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || tableLoading}
                error={error}
              >
                <TableContainer2
                  columns={columns}
                  data={list}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setFilterValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search..."
                  manualSorting={true}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default IssueToLabour;
